import * as React from 'react';
import { Box, Chip, Typography } from '@mui/material';

// Per-table filters. Each table on the page owns its own filter state and its
// own bar, so filtering one table never touches another. Semantics: chips
// within a group OR together (an entity has exactly one stage/type/product, so
// AND-ing inside a group would always return nothing); groups AND together —
// e.g. SCALING + DR + DCOP = rows whose name contains both tokens AND whose
// product is DCOP. Verdict is an optional extra group a table opts into
// (Winners → keep/scale, Needs a Decision → pause/monitor/promote).

export interface TableFilters {
  stage: string[]; // campaign_name contains: SCALING | TESTING
  type: string[]; // campaign_name contains: DR | SLR
  product: string[]; // product starts-with: DCOP | TRAVEL NECK PILLOW
  verdict: string[]; // per-table verdict tokens (optional group)
}

export const EMPTY_FILTERS: TableFilters = {
  stage: [],
  type: [],
  product: [],
  verdict: [],
};

export function anyFilterActive(f: TableFilters) {
  return (
    f.stage.length > 0 || f.type.length > 0 || f.product.length > 0 || f.verdict.length > 0
  );
}

// Name/product predicate shared by campaigns, ads, and testing rows.
export function matchesEntity(
  campaignName: string | undefined,
  product: string | undefined,
  f: TableFilters,
) {
  const name = (campaignName || '').toUpperCase();
  const prod = (product || '').toUpperCase();
  const nameHasAny = (tokens: string[]) => tokens.some(t => name.includes(t));
  if (f.stage.length && !nameHasAny(f.stage)) return false;
  if (f.type.length && !nameHasAny(f.type)) return false;
  // Product match is prefix-based so "DCOP" also matches "DCOP (offer variant)"
  // while staying distinct from "Travel Neck Pillow".
  if (f.product.length && !f.product.some(p => prod.startsWith(p))) return false;
  return true;
}

// Verdict predicate — empty selection matches everything.
export function matchesVerdict(verdict: string | undefined, f: TableFilters) {
  if (!f.verdict.length) return true;
  return f.verdict.includes((verdict || '').toLowerCase());
}

type Option = { value: string; display: string };

function ChipGroup({
  label,
  options,
  selected,
  onToggle,
}: {
  label: string;
  options: Option[];
  selected: string[];
  onToggle: (value: string) => void;
}) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
      <Typography
        variant="caption"
        sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: 0.3 }}
      >
        {label}
      </Typography>
      {options.map(o => {
        const active = selected.includes(o.value);
        return (
          <Chip
            key={o.value}
            label={o.display}
            size="small"
            clickable
            color={active ? 'primary' : 'default'}
            variant={active ? 'filled' : 'outlined'}
            onClick={() => onToggle(o.value)}
          />
        );
      })}
    </Box>
  );
}

const STAGE_OPTIONS: Option[] = [
  { value: 'SCALING', display: 'Scaling' },
  { value: 'TESTING', display: 'Testing' },
];
const TYPE_OPTIONS: Option[] = [
  { value: 'DR', display: 'DR' },
  { value: 'SLR', display: 'SLR' },
];
const PRODUCT_OPTIONS: Option[] = [
  { value: 'DCOP', display: 'DCOP' },
  { value: 'TRAVEL NECK PILLOW', display: 'Travel Neck Pillow' },
  { value: 'COOLING COMFORTER', display: 'Cooling Comforter' },
];

// A single table's filter bar. Pass verdictLabel/verdictOptions to add the
// table-specific verdict group (omit for tables without one).
export default function FilterBar({
  filters,
  onChange,
  verdictLabel,
  verdictOptions,
}: {
  filters: TableFilters;
  onChange: (next: TableFilters) => void;
  verdictLabel?: string;
  verdictOptions?: Option[];
}) {
  const toggle = (key: keyof TableFilters) => (value: string) => {
    const cur = filters[key];
    const next = cur.includes(value) ? cur.filter(v => v !== value) : [...cur, value];
    onChange({ ...filters, [key]: next });
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        columnGap: 2.5,
        rowGap: 1,
        mb: 1,
      }}
    >
      <ChipGroup label="STAGE" options={STAGE_OPTIONS} selected={filters.stage} onToggle={toggle('stage')} />
      <ChipGroup label="TYPE" options={TYPE_OPTIONS} selected={filters.type} onToggle={toggle('type')} />
      <ChipGroup
        label="PRODUCT"
        options={PRODUCT_OPTIONS}
        selected={filters.product}
        onToggle={toggle('product')}
      />
      {verdictOptions && verdictOptions.length > 0 && (
        <ChipGroup
          label={verdictLabel || 'VERDICT'}
          options={verdictOptions}
          selected={filters.verdict}
          onToggle={toggle('verdict')}
        />
      )}
      {anyFilterActive(filters) && (
        <Chip
          label="Clear"
          size="small"
          variant="outlined"
          onClick={() => onChange(EMPTY_FILTERS)}
        />
      )}
    </Box>
  );
}
